import {useState, useEffect, useCallback} from "react";
import {LogOut, RefreshCw, FilePlus, X, Image} from "lucide-react";
import {BrowserRouter as Router} from "react-router-dom";

import {Amplify} from "aws-amplify";
import {
  signUp,
  confirmSignUp,
  signIn,
  signOut,
  getCurrentUser,
} from "aws-amplify/auth";
import {sessionStorage} from "aws-amplify/utils";
import {cognitoUserPoolsTokenProvider} from "aws-amplify/auth/cognito";

import styles from "./App.module.css";

import Loading from "./components/UI/Loading";
import Input from "./components/UI/Input";
import Button from "./components/UI/Button";

import DocumentTypes from "./components/DocumentTypes/DocumentTypes";
import Analysis from "./components/Analysis/Analysis";

import {useForm} from "./components/Hooks/form-hook";

import {
  VALIDATOR_REQUIRE,
  VALIDATOR_EMAIL,
} from "./components/UI/util/validators";

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolClientId: process.env.REACT_APP_AWS_USER_POOL_WEB_CLIENT_ID,
      userPoolId: process.env.REACT_APP_AWS_USER_POOL_ID,
    },
  },
});

const AddDocumentModal = ({isOpen, onClose, onSave}) => {
  const [file, setFile] = useState(null);

  if (!isOpen) return null;

  const handleSave = () => {
    if (!file) {
      document.getElementById("status-message").innerText =
        "Please select an image file to upload.";
      document
        .getElementById("status-message")
        .classList.remove(
          "hidden",
          "bg-red-100",
          "text-red-800",
          "bg-blue-100",
          "text-blue-800",
          "bg-teal-100",
          "text-teal-800"
        );
      document
        .getElementById("status-message")
        .classList.add("bg-yellow-100", "text-yellow-800");
      setTimeout(() => {
        document.getElementById("status-message").classList.add("hidden");
      }, 4000);
      return;
    }

    onSave(file);

    setFile(null);
    onClose();
  };

  const fileName = file ? file.name : "No file selected";

  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden transform transition-all duration-300 scale-100">
        {/* Modal Header */}
        <div className="flex justify-between items-center p-5 border-b border-gray-200 bg-[#275ca7] text-white">
          <h3 className="text-xl font-semibold">Upload Image Document</h3>
          <button
            onClick={onClose}
            className="p-1 rounded-full hover:bg-[#275ca7] transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Body (Form) */}
        <div className="p-6 space-y-6">
          <div className="flex items-center space-x-3 text-gray-600">
            <Image className="w-6 h-6 text-[#275ca7]" />
            <span className="font-semibold">Current File: {fileName}</span>
          </div>
          <div>
            <label
              htmlFor="doc-file"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Select Image File (PNG, JPEG, GIF)
            </label>
            <input
              type="file"
              id="doc-file"
              onChange={(e) => setFile(e.target.files[0] || null)}
              accept="image/*"
              className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-[#275ca7] file:text-white hover:file:bg-[#1e4e8c] transition duration-150 cursor-pointer"
            />
          </div>
        </div>

        {/* Modal Footer (Actions) */}
        <div className="flex justify-end p-5 bg-gray-50 border-t border-gray-200 space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-100 transition duration-150"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 text-sm font-medium text-white bg-[#275ca7] rounded-lg shadow-md hover:bg-[#1e4e8c] transition duration-150"
          >
            Upload Image
          </button>
        </div>
      </div>
    </div>
  );
};

const Header = ({onLogout, onRefresh, onAddDocumentClick, loginState}) => {
  return (
    <header className="bg-white shadow-xl fixed top-0 left-0 right-0 z-10">
      <div className="w-full mx-auto px-8 h-16 flex items-center justify-between">
        <div className="flex items-center">
          <img className="w-[150px]" src="./images/ccx_logo.png" />
        </div>

        {loginState.state === "logged_in" && (
          <div className="flex items-center space-x-4">
            {/* Add Document Button */}
            <button
              onClick={onAddDocumentClick}
              className="flex items-center px-3 py-2.5 text-sm font-medium text-white bg-[#275ca7] rounded-lg shadow-md hover:bg-[#1e4e8c] transition duration-150 ease-in-out"
            >
              <FilePlus className="h-4 w-4" />
            </button>
            {/* Refresh Button */}
            <div className="flex items-center text-sm text-gray-400">
              <button
                onClick={onRefresh}
                className="flex items-center px-3 py-2.5 text-sm font-medium text-white bg-[#275ca7] rounded-lg shadow-md hover:bg-[#1e4e8c] transition duration-150 ease-in-out"
              >
                <RefreshCw className="h-4 w-4" />
              </button>
            </div>

            {/* Logout Button */}
            <button
              onClick={onLogout}
              className="flex items-center px-3 py-2 text-sm font-medium text-white bg-red-600 rounded-lg shadow-md hover:bg-red-700 transition duration-150 ease-in-out"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </button>
          </div>
        )}
      </div>
    </header>
  );
};

function App() {
  const [files, setFiles] = useState([]);
  const [csvFiles, setCsvFiles] = useState([]);
  const [selectedType, setSelectedType] = useState([]);
  const [refreshData, setRefreshData] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [formState, inputHandler] = useForm(
    {
      firstname: {
        value: "",
        isValid: false,
      },
      lastname: {
        value: "",
        isValid: false,
      },
      email: {
        value: "",
        isValid: false,
      },
      password: {
        value: "",
        isValid: false,
      },
      confirm_password: {
        value: "",
        isValid: false,
      },
    },
    false
  );

  const [confirmationFormState, confirmationInputHandler] = useForm(
    {
      email: {
        value: "",
        isValid: false,
      },
      confirmationCode: {
        value: "",
        isValid: false,
      },
    },
    false
  );

  const [loginFormState, loginInputHandler] = useForm(
    {
      email: {
        value: "",
        isValid: false,
      },
      password: {
        value: "",
        isValid: false,
      },
    },
    false
  );

  const [loginState, setLoginState] = useState({
    state: null,
    message: null,
  });

  const handleSignUp = async () => {
    console.log(`Firstname: ${formState.inputs.firstname.value}`);
    console.log(`Lastname: ${formState.inputs.lastname.value}`);
    console.log(`Email: ${formState.inputs.email.value}`);
    console.log(`Password: ${formState.inputs.password.value}`);
    console.log(`Confirm Password: ${formState.inputs.confirm_password.value}`);

    if (
      formState.inputs.password.value !==
      formState.inputs.confirm_password.value
    ) {
      setLoginState({
        state: "signup",
        message: "Passwords do not match.",
      });
      return;
    }

    setIsActionLoading(true);
    try {
      const {isSignUpComplete, userId, nextStep} = await signUp({
        username: formState.inputs.email.value,
        password: formState.inputs.password.value,
        options: {
          userAttributes: {
            given_name: formState.inputs.firstname.value,
            family_name: formState.inputs.lastname.value,
          },
          autoSignIn: true,
        },
      });

      console.log(`User Id: ${userId}`);
      console.log(`Next Step: ${JSON.stringify(nextStep)}`);
      console.log(`Is Signup Complete: ${isSignUpComplete}`);

      if (nextStep.signUpStep === "CONFIRM_SIGN_UP") {
        setLoginState({
          state: "confirm",
          message: `Confirm Email. Check you email for the confirmation code.`,
        });
        inputHandler("firstname", "", false);
        inputHandler("lastname", "", false);
        inputHandler("email", "", false);
        inputHandler("password", "", false);
        inputHandler("confirm_password", "", false);
      }
      setIsActionLoading(false);
    } catch (error) {
      console.log("error signing up:", error.message);
      if (
        error.message.includes(
          "Password did not conform with policy: Password must have symbol characters"
        )
      ) {
        setLoginState({
          state: "signup",
          message:
            "Password did not conform with policy: Password must have symbol characters",
        });
      } else if (error.message.includes("User already exists")) {
        setLoginState({
          state: "signup",
          message: "User already exists",
        });
      } else {
        setLoginState({
          state: "signup",
          message: "Unknown error occurred while signing up. Please try again.",
        });
      }
      setIsActionLoading(false);
    }
  };

  const handleSignUpConfirmation = async () => {
    console.log(`Email: ${confirmationFormState.inputs.email.value}`);
    console.log(
      `Confirmation Code: ${confirmationFormState.inputs.confirmationCode.value}`
    );

    setIsActionLoading(true);
    try {
      const {isSignUpComplete, nextStep} = await confirmSignUp({
        username: confirmationFormState.inputs.email.value,
        confirmationCode: confirmationFormState.inputs.confirmationCode.value,
      });
      console.log(`Is Signup Complete: ${isSignUpComplete}`);
      console.log(`Next Step: ${JSON.stringify(nextStep)}`);
      if (
        nextStep.signUpStep === "DONE" ||
        nextStep.signUpStep === "COMPLETE_AUTO_SIGN_IN"
      ) {
        setLoginState({
          state: "login",
        });
        confirmationInputHandler("email", "", false);
        confirmationInputHandler("confirmationCode", "", false);
      }
      setIsActionLoading(false);
    } catch (error) {
      console.log("error confirming sign up", error.message);
      if (
        error.message ===
        "Invalid verification code provided, please try again."
      ) {
        setLoginState({
          state: "confirm",
          message: "Invalid verification code provided, please try again.",
        });
      }
      setIsActionLoading(false);
    }
  };

  const handleLogin = async () => {
    setIsActionLoading(true);
    try {
      const {isSignedIn, nextStep} = await signIn({
        username: loginFormState.inputs.email.value,
        password: loginFormState.inputs.password.value,
      });

      console.log(`isSignedIn: ${isSignedIn}`);
      console.log(`Next Step: ${JSON.stringify(nextStep)}`);
      if (nextStep.signInStep === "CONFIRM_SIGN_UP") {
        setLoginState({
          state: "confirm",
          message: `Email not confirmed. Check you email for the confirmation code.`,
        });
      }
      if (nextStep.signInStep === "DONE") {
        cognitoUserPoolsTokenProvider.setKeyValueStorage(sessionStorage);
        setLoginState({
          state: "logged_in",
        });
      }
      loginInputHandler("email", "", false);
      loginInputHandler("password", "", false);
      setIsActionLoading(false);
    } catch (error) {
      console.log("error signing in", error.message);
      if (error.message === "Incorrect username or password.") {
        setLoginState({
          state: "login",
          message: "Incorrect username or password.",
        });
      }
      setIsActionLoading(false);
    }
  };

  const currentAuthenticatedUser = async () => {
    setIsLoading(true);
    try {
      const {username, userId, signInDetails} = await getCurrentUser();
      console.log(`The username: ${username}`);
      console.log(`The userId: ${userId}`);
      console.log(`The signInDetails: ${JSON.stringify(signInDetails)}`);
      if (signInDetails.loginId) {
        setLoginState({
          state: "logged_in",
        });
      } else {
        setLoginState({
          state: "login",
        });
      }
      setIsLoading(false);
    } catch (err) {
      console.log(`Error getting current user: ${err.message}`);
      setLoginState({
        state: "login",
      });
      setIsLoading(false);
    }
  };

  const handleSignOut = async () => {
    setIsLoading(true);
    try {
      await signOut();

      setLoginState({
        state: "login",
        message: null,
      });
      setIsLoading(false);
    } catch (error) {
      console.log("error signing out: ", error);
      setIsLoading(false);
    }
  };

  const handleAddDocumentClick = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const handleSaveDocument = async (file) => {
    console.log("File to be uploaded: ", file);

    var reader = new FileReader();
    reader.onload = async function (e) {
      let document_type = file.type;
      let document_content = reader.result.replace(
        `data:${document_type};base64,`,
        ""
      );

      console.log(`Document Content: ${document_content}`);
      //   await uploadDocument(
      //     uploadFormState.inputs.file.value.name,
      //     document_content,
      //     document_type
      //   );

      document.getElementById(
        "status-message"
      ).innerText = `Image "${file.name}" uploaded!`;
      document
        .getElementById("status-message")
        .classList.remove(
          "hidden",
          "bg-red-100",
          "text-red-800",
          "bg-blue-100",
          "text-blue-800",
          "bg-yellow-100",
          "text-yellow-800"
        );
      document
        .getElementById("status-message")
        .classList.add("bg-teal-100", "text-teal-800");
      setTimeout(() => {
        document.getElementById("status-message").classList.add("hidden");
      }, 3000);
    };
    reader.readAsDataURL(file);
  };

  useEffect(() => {
    currentAuthenticatedUser();
  }, []);

  const updateFilesHandler = (value) => {
    setFiles(value);
  };

  const updateCsvFilesHandler = (value) => {
    setCsvFiles(value);
  };

  const updateSelectedTypeHandler = (value) => {
    setSelectedType(value);
  };

  return (
    <div className={`${styles.App}`}>
      <Header
        onLogout={handleSignOut}
        onRefresh={() => setRefreshData(true)}
        onAddDocumentClick={handleAddDocumentClick}
        loginState={loginState}
      />

      {/* Status Message Display */}
      <div
        id="status-message"
        className="hidden fixed top-20 right-0 transform -translate-x-1/2 p-3 rounded-lg text-sm font-semibold shadow-xl transition-opacity duration-300 z-50"
        role="alert"
      >
        Status Message
      </div>

      <section className="flex flex-col items-center justify-center pt-16 min-h-screen bg-white w-full">
        {loginState.state === "signup" && !isLoading && (
          <div className={`${styles.App__Login}`}>
            <div className={`${styles.App__Login__Segment}`}>
              <Input
                id="firstname"
                element="input"
                type="text"
                placeholder="First Name"
                variant="primary"
                validators={[VALIDATOR_REQUIRE()]}
                onInput={inputHandler}
                value={formState.inputs.firstname.value}
                valid={formState.inputs.firstname.isValid}
              />
            </div>
            <div className={`${styles.App__Login__Segment}`}>
              <Input
                id="lastname"
                element="input"
                type="text"
                placeholder="Last Name"
                variant="primary"
                validators={[VALIDATOR_REQUIRE()]}
                onInput={inputHandler}
                value={formState.inputs.lastname.value}
                valid={formState.inputs.lastname.isValid}
              />
            </div>
            <div className={`${styles.App__Login__Segment}`}>
              <Input
                id="email"
                element="input"
                type="text"
                placeholder="Email"
                variant="primary"
                validators={[VALIDATOR_REQUIRE(), VALIDATOR_EMAIL()]}
                onInput={inputHandler}
                value={formState.inputs.email.value}
                valid={formState.inputs.email.isValid}
              />
            </div>
            <div className={`${styles.App__Login__Segment}`}>
              <Input
                id="password"
                element="input"
                type="password"
                placeholder="Password"
                variant="primary"
                validators={[VALIDATOR_REQUIRE()]}
                onInput={inputHandler}
                value={formState.inputs.password.value}
                valid={formState.inputs.password.isValid}
              />
            </div>
            <div className={`${styles.App__Login__Segment}`}>
              <Input
                id="confirm_password"
                element="input"
                type="password"
                placeholder="Confirm Password"
                variant="primary"
                validators={[VALIDATOR_REQUIRE()]}
                onInput={inputHandler}
                value={formState.inputs.confirm_password.value}
                valid={formState.inputs.confirm_password.isValid}
              />
            </div>
            <div className={`${styles.App__Login__Segment}`}>
              <div className={`${styles.App__Login__Button}`}>
                <Button onClick={handleSignUp} disabled={!formState.isValid}>
                  {isActionLoading === true ? (
                    <img
                      style={{width: "20px", height: "20px"}}
                      src="./images/three-dots.svg"
                    />
                  ) : (
                    "SIGN UP"
                  )}
                </Button>
              </div>
              <div className={`${styles.App__Login__Button}`}>
                <Button
                  onClick={() =>
                    setLoginState({
                      state: "login",
                      message: null,
                    })
                  }
                >
                  LOGIN
                </Button>
              </div>
            </div>

            {loginState.message !== null && (
              <div className={`${styles.App__Login__Segment}`}>
                <h6>{loginState.message}</h6>
              </div>
            )}

            <div className={`${styles.App__Login__Segment}`}>
              <h5
                onClick={() =>
                  setLoginState({
                    state: "confirm",
                    message: null,
                  })
                }
              >
                Already have a confirmation code? Confirm your account
              </h5>
            </div>
          </div>
        )}

        {loginState.state === "login" && !isLoading && (
          <div className={`${styles.App__Login}`}>
            <div className={`${styles.App__Login__Segment}`}>
              <Input
                id="email"
                element="input"
                type="text"
                placeholder="Email"
                variant="primary"
                validators={[VALIDATOR_REQUIRE(), VALIDATOR_EMAIL()]}
                onInput={loginInputHandler}
                value={loginFormState.inputs.email.value}
                valid={loginFormState.inputs.email.isValid}
              />
            </div>
            <div className={`${styles.App__Login__Segment}`}>
              <Input
                id="password"
                element="input"
                type="password"
                placeholder="Password"
                variant="primary"
                validators={[VALIDATOR_REQUIRE()]}
                onInput={loginInputHandler}
                value={loginFormState.inputs.password.value}
                valid={loginFormState.inputs.password.isValid}
              />
            </div>
            <div className={`${styles.App__Login__Segment}`}>
              <div className={`${styles.App__Login__Button}`}>
                <Button
                  onClick={handleLogin}
                  disabled={!loginFormState.isValid}
                >
                  {isActionLoading === true ? (
                    <img
                      style={{width: "20px", height: "20px"}}
                      src="./images/three-dots.svg"
                    />
                  ) : (
                    "LOGIN"
                  )}
                </Button>
              </div>
            </div>

            {loginState.message !== null && (
              <div className={`${styles.App__Login__Segment}`}>
                <h6>{loginState.message}</h6>
              </div>
            )}

            <div className={`${styles.App__Login__Segment}`}>
              <h5
                onClick={() =>
                  setLoginState({
                    state: "signup",
                    message: null,
                  })
                }
              >
                Sign up for a new account
              </h5>
            </div>
          </div>
        )}

        {loginState.state === "confirm" && !isLoading && (
          <div className={`${styles.App__Login}`}>
            <div className={`${styles.App__Login__Segment}`}>
              <Input
                id="email"
                element="input"
                type="text"
                placeholder="Email"
                variant="primary"
                validators={[VALIDATOR_REQUIRE(), VALIDATOR_EMAIL()]}
                onInput={confirmationInputHandler}
                value={confirmationFormState.inputs.email.value}
                valid={confirmationFormState.inputs.email.isValid}
              />
            </div>
            <div className={`${styles.App__Login__Segment}`}>
              <Input
                id="confirmationCode"
                element="input"
                placeholder="Confirmation Code"
                variant="primary"
                validators={[VALIDATOR_REQUIRE()]}
                onInput={confirmationInputHandler}
                value={confirmationFormState.inputs.confirmationCode.value}
                valid={confirmationFormState.inputs.confirmationCode.isValid}
              />
            </div>
            <div className={`${styles.App__Login__Segment}`}>
              <div className={`${styles.App__Login__Button}`}>
                <Button
                  onClick={handleSignUpConfirmation}
                  disabled={!confirmationFormState.isValid}
                >
                  {isActionLoading === true ? (
                    <img
                      style={{width: "20px", height: "20px"}}
                      src="./images/three-dots.svg"
                    />
                  ) : (
                    "CONFIRM"
                  )}
                </Button>
              </div>
              <div className={`${styles.App__Login__Button}`}>
                <Button
                  onClick={() =>
                    setLoginState({
                      state: "login",
                      message: null,
                    })
                  }
                >
                  LOGIN
                </Button>
              </div>
            </div>

            {loginState.message !== null && (
              <div className={`${styles.App__Login__Segment}`}>
                <h6>{loginState.message}</h6>
              </div>
            )}
          </div>
        )}

        {loginState.state === "logged_in" && !isLoading && (
          <div className={`${styles.App__Dashboard__Container}`}>
            <div className={`${styles.Left__Section}`}>
              <DocumentTypes
                updateFiles={updateFilesHandler}
                updateCsvFiles={updateCsvFilesHandler}
                updateSelectedType={updateSelectedTypeHandler}
                selectedType={selectedType}
                refreshData={refreshData}
                updateRefreshData={() => setRefreshData(false)}
              />
            </div>
            <div className={`${styles.Right__Section}`}>
              <Router>
                <Analysis
                  files={files}
                  csvFiles={csvFiles}
                  selectedType={selectedType}
                />
              </Router>
            </div>
          </div>
        )}

        {(isLoading || loginState.state === null) && (
          <div className={`${styles.App__Loading}`}>
            <Loading />
          </div>
        )}
      </section>

      {/* Document Creation Modal */}
      <AddDocumentModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveDocument}
      />
    </div>
  );
}

export default App;
